import type { Part } from "./types";

export interface PlacedPart {
  part: Part;
  /** X (cm) on the sheet, measured from the sheet's bottom-left. */
  x: number;
  /** Y (cm) on the sheet. */
  y: number;
  /** True if part was rotated 90° (its width axis now runs along sheet length). */
  rotated: boolean;
  /** Drawn width on the sheet (post-rotation). */
  drawnWidth: number;
  /** Drawn length on the sheet (post-rotation). */
  drawnLength: number;
}

export interface Sheet {
  /** Sheet number, starting at 1. */
  index: number;
  /** Sheet width (shorter dim) in cm. */
  width: number;
  /** Sheet length (longer dim) in cm. */
  length: number;
  /** Stock thickness this sheet represents. */
  thickness: number;
  placements: PlacedPart[];
  /** Used area (sum of part bounding boxes). */
  usedArea: number;
}

/**
 * MAXRECTS-style first-fit-decreasing bin packer with rotation, kerf-aware.
 *
 * - One sheet collection per unique thickness (you can't cut 1/4" from a 3/4" sheet).
 * - Parts are sorted by max-edge descending then placed into the first rect that fits.
 * - When placed, the rect is split into right + bottom remainders (guillotine-friendly).
 * - Kerf is added to placed dimensions so subsequent parts include saw clearance.
 *
 * For an MVP this is pragmatic and produces tight, operator-friendly layouts.
 * Trapezoidal (`angledTop`) parts are placed using their bounding rectangle —
 * the diagonal trim is shown on the cut diagram so the operator knows where to cut.
 */
export function packParts(
  parts: Part[],
  sheetWidth: number,
  sheetLength: number,
  kerf: number,
): Sheet[] {
  // group by thickness
  const byThickness = new Map<number, Part[]>();
  for (const p of parts) {
    const arr = byThickness.get(p.thickness) ?? [];
    arr.push(p);
    byThickness.set(p.thickness, arr);
  }

  const sheets: Sheet[] = [];
  let sheetIndex = 1;

  for (const [thickness, group] of byThickness) {
    // sort largest-first by longest edge
    const sorted = [...group].sort(
      (a, b) => Math.max(b.width, b.length) - Math.max(a.width, a.length),
    );

    const localSheets: Array<{ sheet: Sheet; freeRects: Rect[] }> = [];

    for (const part of sorted) {
      let placed = false;
      for (const ls of localSheets) {
        if (tryPlace(part, ls, kerf)) {
          placed = true;
          break;
        }
      }
      if (!placed) {
        const sheet: Sheet = {
          index: sheetIndex++,
          width: sheetWidth,
          length: sheetLength,
          thickness,
          placements: [],
          usedArea: 0,
        };
        const ls = {
          sheet,
          freeRects: [{ x: 0, y: 0, w: sheetLength, h: sheetWidth }],
        };
        localSheets.push(ls);
        if (!tryPlace(part, ls, kerf)) {
          // Part is bigger than a sheet — record on its own sheet (operator will handle).
          sheet.placements.push({
            part,
            x: 0,
            y: 0,
            rotated: false,
            drawnLength: Math.min(part.length, sheetLength),
            drawnWidth: Math.min(part.width, sheetWidth),
          });
        }
      }
    }
    for (const ls of localSheets) sheets.push(ls.sheet);
  }

  return sheets;
}

interface Rect {
  x: number;
  y: number;
  w: number; // along sheet length
  h: number; // along sheet width
}

function tryPlace(
  part: Part,
  ls: { sheet: Sheet; freeRects: Rect[] },
  kerf: number,
): boolean {
  // Two orientations: (length along sheet length) and rotated 90°.
  const opts = [
    { w: part.length, h: part.width, rotated: false },
    { w: part.width, h: part.length, rotated: true },
  ];

  type Best = { rect: Rect; opt: (typeof opts)[number]; idx: number };
  let best: Best | null = null;
  let bestScore = Infinity; // best short-side fit
  for (let idx = 0; idx < ls.freeRects.length; idx++) {
    const rect = ls.freeRects[idx];
    for (const opt of opts) {
      if (opt.w <= rect.w && opt.h <= rect.h) {
        const score = Math.min(rect.w - opt.w, rect.h - opt.h);
        if (score < bestScore) {
          bestScore = score;
          best = { rect, opt, idx };
        }
      }
    }
  }

  if (!best) return false;
  const { rect, opt, idx } = best;
  ls.sheet.placements.push({
    part,
    x: rect.x,
    y: rect.y,
    rotated: opt.rotated,
    drawnLength: opt.w,
    drawnWidth: opt.h,
  });
  ls.sheet.usedArea += opt.w * opt.h;

  // Guillotine split: replace used rect with right + bottom remainders.
  const right: Rect = {
    x: rect.x + opt.w + kerf,
    y: rect.y,
    w: rect.w - opt.w - kerf,
    h: opt.h,
  };
  const bottom: Rect = {
    x: rect.x,
    y: rect.y + opt.h + kerf,
    w: rect.w,
    h: rect.h - opt.h - kerf,
  };
  ls.freeRects.splice(idx, 1);
  if (right.w > 0 && right.h > 0) ls.freeRects.push(right);
  if (bottom.w > 0 && bottom.h > 0) ls.freeRects.push(bottom);
  return true;
}

export interface CutPlanSummary {
  sheetCount: number;
  totalArea: number;
  usedArea: number;
  wastePercent: number;
  partCount: number;
}

export function summarize(sheets: Sheet[]): CutPlanSummary {
  const totalArea = sheets.reduce((s, sh) => s + sh.width * sh.length, 0);
  const usedArea = sheets.reduce((s, sh) => s + sh.usedArea, 0);
  const partCount = sheets.reduce((s, sh) => s + sh.placements.length, 0);
  return {
    sheetCount: sheets.length,
    totalArea,
    usedArea,
    wastePercent:
      totalArea === 0 ? 0 : ((totalArea - usedArea) / totalArea) * 100,
    partCount,
  };
}
